// Global handler of last resort for axios HTTP rejections nobody caught
// (PORTAL-CLIENT-608 6NT 6P0 6P7 6PK 6PP 6PX 6Q3 5ZF 5ZK): the user gets a
// toast instead of a silently dead page, and the matching Sentry relabeling
// lives in sentryAxiosFingerprint.js. Network/timeout errors (no response)
// stay untouched — Sentry's ignoreErrors already covers them.
// CommonJS on purpose, same as sentryAxiosFingerprint.js, so plain `node`
// can require it (see unhandledHttpErrors.verify.js).

const DEDUPE_WINDOW_MS = 5000

function describeAxiosHttpError(reason) {
  if (!reason || reason.isAxiosError !== true) return null
  const status = reason.response && reason.response.status
  if (!status) return null
  const message = status >= 500
    ? `The server couldn't complete your last request (HTTP ${status}). Please try again.`
    : `Your last request couldn't be completed (HTTP ${status}).`
  return { status, message }
}

function createUnhandledRejectionHandler(opts) {
  const { showErrorToast, now = () => Date.now() } = opts || {}
  let lastMessage = null
  let lastShownAt = 0

  return function onUnhandledRejection(event) {
    const info = describeAxiosHttpError(event && event.reason)
    if (!info) return

    // A burst of parallel failures (one page firing several calls) should
    // surface as one toast, not a stack of identical ones.
    const ts = now()
    if (info.message === lastMessage && ts - lastShownAt < DEDUPE_WINDOW_MS) return
    lastMessage = info.message
    lastShownAt = ts

    try {
      if (typeof showErrorToast === 'function') showErrorToast(info.message)
    } catch (e) {
      // the surface must never become an error source itself
    }
  }
}

let installed = false
let surfaceWired = false

function installUnhandledHttpErrorSurface(opts) {
  // Record whether a real toast fn was wired, before the window guard, so the
  // flag reflects app configuration even where we can't attach the listener
  // (SSR / node verify). The Sentry relabel (sentryAxiosFingerprint) reads this.
  surfaceWired = typeof (opts && opts.showErrorToast) === 'function'
  if (installed || typeof window === 'undefined') return
  installed = true
  window.addEventListener('unhandledrejection', createUnhandledRejectionHandler(opts))
}

// True once the surface has been installed with a real toast function. Guards
// the Sentry relabel so an uncaught axios rejection is never marked "handled"
// when the user in fact got no feedback (which would hide it from triage).
function isHttpErrorSurfaceWired() {
  return surfaceWired
}

module.exports = {
  describeAxiosHttpError,
  createUnhandledRejectionHandler,
  installUnhandledHttpErrorSurface,
  isHttpErrorSurfaceWired,
}
